import { useState } from "react";
import { Mail, CheckCircle2, Sparkles } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * REBELTEAM signup — výrazný hero-style form pro footer (a kdekoliv jinde).
 * Větší než původní decentní řádek, ale stále drží edgy/dark estetiku webu.
 */
export default function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [gdpr, setGdpr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.includes("@") || !email.includes(".")) return setError("E-mail musí obsahovat @ a doménu (např. tvoje@seznam.cz)");
    if (!gdpr) return setError("Souhlas s GDPR je povinný");
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        gdpr_consent: gdpr,
      });
      setDone(true);
      setEmail("");
      setGdpr(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Nepodařilo se přihlásit");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="newsletter-success"
        className="max-w-2xl mx-auto text-center px-6 py-10 rounded-2xl"
        style={{
          background: "linear-gradient(135deg, rgba(255,0,90,0.15), rgba(0,229,255,0.08))",
          border: "1px solid rgba(255,0,90,0.4)",
        }}
      >
        <CheckCircle2 className="w-12 h-12 text-[#FF005A] mx-auto mb-3" />
        <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "1.4rem", color: "#FFFFFF", letterSpacing: "-0.02em" }}>
          VÍTEJ V REBELTEAMU ★
        </p>
        <p className="text-zinc-300 text-sm mt-2 font-medium">
          Sleduj svou schránku — první potvrzovací e-mail jde právě teď.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="newsletter-form"
      className="max-w-2xl mx-auto text-center"
    >
      {/* Eye-brow badge */}
      <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full" style={{ background: "rgba(255,0,90,0.1)", border: "1px solid rgba(255,0,90,0.3)" }}>
        <Sparkles className="w-3 h-3 text-[#FF005A]" />
        <p className="text-[10px] tracking-[0.25em] font-bold text-[#FF005A] uppercase">
          REBELTEAM · Fan Club
        </p>
      </div>

      {/* Big headline */}
      <h2
        style={{
          fontFamily: "Unbounded, sans-serif",
          fontWeight: 900,
          fontSize: "clamp(1.5rem, 4vw, 2.6rem)",
          color: "#FFFFFF",
          textTransform: "uppercase",
          letterSpacing: "-0.03em",
          lineHeight: 1.05,
          marginBottom: "0.5rem",
        }}
      >
        Připoj se k <span style={{ color: "#FF005A" }}>REBELTEAMU</span>
      </h2>
      <p className="text-zinc-400 text-sm sm:text-base mb-6 max-w-md mx-auto font-medium" style={{ fontFamily: "Manrope, sans-serif" }}>
        Buď první, kdo se dozví o nových koncertech, písničkách a exkluzivních věcech jen pro fanoušky.
      </p>

      {/* Input row */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3 max-w-lg mx-auto">
        <div className="flex-1 relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tvuj@email.cz"
            data-testid="newsletter-email-input"
            className="w-full pl-9 pr-3 py-3 bg-[#0A0A0A] border border-white/15 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#FF005A] transition-colors text-sm rounded-md"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="newsletter-submit-btn"
          className="px-6 py-3 bg-[#FF005A] hover:bg-[#FF1A6A] disabled:opacity-50 text-white text-xs font-extrabold tracking-[0.15em] uppercase transition-all whitespace-nowrap rounded-md shadow-lg shadow-[#FF005A]/30"
          style={{ fontFamily: "Unbounded, sans-serif" }}
        >
          {loading ? "Přihlašuji…" : "★ Přihlásit"}
        </button>
      </div>

      {/* GDPR consent */}
      <label className="flex items-start gap-2 text-[11px] text-zinc-500 cursor-pointer max-w-md mx-auto text-left mt-2">
        <input
          type="checkbox"
          checked={gdpr}
          onChange={(e) => setGdpr(e.target.checked)}
          data-testid="newsletter-gdpr-checkbox"
          className="mt-0.5 accent-[#FF005A] w-3.5 h-3.5"
        />
        <span>
          Souhlasím se zasíláním novinek a se{" "}
          <a href="/gdpr" className="text-zinc-300 hover:text-[#FF005A] underline-offset-2 hover:underline">
            zpracováním osobních údajů (GDPR)
          </a>
          . Můžeš se kdykoliv odhlásit jedním kliknutím.
        </span>
      </label>

      {error && (
        <p data-testid="newsletter-error" className="text-xs text-red-400 mt-3 font-semibold">
          ⚠ {error}
        </p>
      )}
    </form>
  );
}
